import { useReducer } from 'react';

/**
 * Step machine of the PIN sheet (TECH-663). Pure: no timers, no effects, no network. The sheet
 * owns those and tells the machine what happened.
 */

/**
 * The line under the PIN field, which is about the PIN only. Any other failure is said once in a
 * toast (`joinFailureToast`), as on every PIN field since #1299, and leaves no line. A rate limit
 * is not a line under the field: it locks the card (`PinStep.rateLimited`), and the field goes.
 */
export type PinError = 'wrong';
export type ChipTone = 'joined' | 'requested';
export interface Chip {
  tone: ChipTone;
  spaceName: string;
}
/**
 * How a card ended. `joined`: the person is in. `pin_pending` and `requested`: the request waits
 * on the host (the membership is still pending, as `isPendingMembership` reads it). `closed`: the
 * request stopped waiting while the card was open (a host declined it, or it is gone), so there is
 * neither a membership nor a request.
 */
export type AnswerOutcome = 'joined' | 'pin_pending' | 'requested' | 'closed';
export interface PinStep {
  kind: 'pin';
  index: number;
  /** Wrong attempts on this card, for the `wrong_attempts` analytics field. */
  wrong: number;
  /**
   * Sticky for this card: a 429 was seen, and the card is locked. The backend refuses every
   * attempt for the rest of its window (15 minutes, longer than the 10-minute hold), so no PIN
   * may be sent from it again; Request access is the way on.
   */
  rateLimited: boolean;
  submitting: boolean;
  error: PinError | null;
  chip: Chip | null;
}
export interface AnswerStep {
  kind: 'answer';
  index: number;
  outcome: AnswerOutcome;
}
export interface TicketStep {
  kind: 'ticket';
  chip: Chip | null;
}
export interface DoneStep {
  kind: 'done';
}
export type Step = PinStep | AnswerStep | TicketStep | DoneStep;

export type EventPinAction =
  | { type: 'submit' }
  | { type: 'wrong_pin' }
  | { type: 'rate_limited' }
  | { type: 'error' }
  | { type: 'joined' }
  | { type: 'pin_pending' }
  /**
   * The request went to the host: the button or a dismiss (the sheet sends those only when
   * `canRequest`), or the PIN's own answer (an unlisted 403, a 409 JOIN_REQUEST_PENDING).
   */
  | { type: 'requested' }
  /** 409 JOIN_REQUEST_NOT_PENDING: a host decided the request meanwhile, or it is gone. */
  | { type: 'closed' }
  | { type: 'advance' };

export interface EventPinState {
  spaceNames: readonly string[];
  hasTicket: boolean;
  step: Step;
}

function freshCard(index: number, chip: Chip | null): PinStep {
  return { kind: 'pin', index, wrong: 0, rateLimited: false, submitting: false, error: null, chip };
}

export function initialEventPinState(
  spaces: readonly { space_name: string }[],
  hasTicket: boolean,
): EventPinState {
  return { spaceNames: spaces.map(s => s.space_name), hasTicket, step: freshCard(0, null) };
}

/** Request access is the primary button after 2 wrong PINs or a rate limit. */
export function requestIsPrimary(step: Step): boolean {
  return step.kind === 'pin' && (step.wrong >= 2 || step.rateLimited);
}

/**
 * Request access may be sent from this step: a card with no PIN being checked (its answer is on
 * the way, and a request now would race it). A locked card may: Request access is its way on.
 */
export function canRequest(step: Step): step is PinStep {
  return step.kind === 'pin' && !step.submitting;
}

/** A PIN may be sent from this step: a card, no PIN being checked, not locked out. */
export function canSubmitPin(step: Step): step is PinStep {
  return step.kind === 'pin' && !step.submitting && !step.rateLimited;
}

function answer(state: EventPinState, step: PinStep, outcome: AnswerOutcome): EventPinState {
  return { ...state, step: { kind: 'answer', index: step.index, outcome } };
}

export function eventPinReducer(state: EventPinState, action: EventPinAction): EventPinState {
  const { step } = state;
  if (step.kind === 'ticket') {
    return action.type === 'advance' ? { ...state, step: { kind: 'done' } } : state;
  }
  if (step.kind === 'answer') {
    if (action.type !== 'advance') return state;
    // A closed request leaves nothing to show on the next step: no membership, no request.
    const chip: Chip | null =
      step.outcome === 'closed'
        ? null
        : {
            tone: step.outcome === 'joined' ? 'joined' : 'requested',
            spaceName: state.spaceNames[step.index] ?? '',
          };
    const next = step.index + 1;
    if (next < state.spaceNames.length) return { ...state, step: freshCard(next, chip) };
    return { ...state, step: state.hasTicket ? { kind: 'ticket', chip } : { kind: 'done' } };
  }
  if (step.kind !== 'pin') return state;

  switch (action.type) {
    case 'submit':
      return step.rateLimited
        ? state
        : { ...state, step: { ...step, submitting: true, error: null } };
    case 'wrong_pin':
      return {
        ...state,
        step: { ...step, submitting: false, error: 'wrong', wrong: step.wrong + 1 },
      };
    case 'rate_limited':
      return {
        ...state,
        step: { ...step, submitting: false, error: null, rateLimited: true },
      };
    case 'error':
      // Said in a toast; the line under the field is about the PIN, so it is cleared.
      return { ...state, step: { ...step, submitting: false, error: null } };
    case 'joined':
      return answer(state, step, 'joined');
    case 'pin_pending':
      return answer(state, step, 'pin_pending');
    case 'closed':
      return answer(state, step, 'closed');
    case 'requested':
      return answer(state, step, 'requested');
    default:
      return state;
  }
}

/** Thin wrapper so the sheet has one import. */
export function useEventPinSteps(spaces: readonly { space_name: string }[], hasTicket: boolean) {
  return useReducer(eventPinReducer, undefined, () => initialEventPinState(spaces, hasTicket));
}
